import { createRoute, Outlet, redirect } from "@tanstack/react-router";
import { appRoute } from "./parent-routes";
import PlaceholderPage from "@phrmpc/shared/components/PlaceholderPage";
import ConcernsIssuesSearchForm from "../../modules/deployment/pages/ConcernsIssuesSearchForm";
import DailyActivityMonitoringLogSearchForm from "../../modules/deployment/pages/DailyActivityMonitoringLogSearchForm";
import DailyActivityMonitoringLogForm from "../../modules/deployment/pages/DailyActivityMonitoringLogForm";
import MonthlyCoveragePlanSearchForm from "@phrmpc/modules/deployment/pages/MonthlyCoveragePlanSearchForm";
import MonthlyCoveragePlanForm from "@phrmpc/modules/deployment/pages/MonthlyCoveragePlanForm";
import MyCalendar from "@phrmpc/modules/deployment/pages/MyCalendar";
import ScheduleAndSiteChangeRequestForm from "@phrmpc/modules/deployment/pages/ScheduleAndSiteChangeRequestForm";
import ScheduleAndSiteChangeRequestSearchForm from "@phrmpc/modules/deployment/pages/ScheduleAndSiteChangeRequestSearchForm";
import { createMonthlyCoveragePlan, getAllAreas, MonthlyCoveragePlanDto } from "@phrmpc/infrastructure/phrmpc";

const deploymentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "deployment",
  staticData: {
    handle: { title: "Deployment" },
  },
});

const dailyActivityMonitoringLogRoute = createRoute({
  getParentRoute: () => deploymentRoute,
  path: "daily-activity-monitoring-logs",
  component: Outlet,
  staticData: {
    handle: { title: "Daily Activity Monitoring" },
  },
});

const dailyActivityMonitoringLogIndexRoute = createRoute({
  getParentRoute: () => dailyActivityMonitoringLogRoute,
  path: "/",
  component: DailyActivityMonitoringLogSearchForm,
  staticData: {
    handle: { title: "Daily Activity Monitoring" },
  },
});

const dailyActivityMonitoringLogDisplayRoute = createRoute({
  getParentRoute: () => dailyActivityMonitoringLogRoute,
  path: "$id/display",
  component: DailyActivityMonitoringLogForm,
  staticData: {
    handle: { title: "View Daily Activity Monitoring Log", action: "display" },
  },
});

const dailyActivityMonitoringLogEditRoute = createRoute({
  getParentRoute: () => dailyActivityMonitoringLogRoute,
  path: "$id/edit",
  component: DailyActivityMonitoringLogForm,
  staticData: {
    handle: { title: "Edit Daily Activity Monitoring Log", action: "edit" },
  },
});

const concernsIssuesRoute = createRoute({
  getParentRoute: () => deploymentRoute,
  path: "concerns-issues",
  component: ConcernsIssuesSearchForm,
  staticData: {
    handle: { title: "Concerns and Issues" },
  },
});

const monthlyCoveragePlanSearchRoute = createRoute({
  getParentRoute: () => deploymentRoute,
  path: "monthly-coverage-plan",
  component: Outlet,
  staticData: {
    handle: { title: "Monthly Coverage Plan" },
  },
});

const monthlyCoveragePlanMyCalendarRoute = createRoute({
  getParentRoute: () => deploymentRoute,
  path: "my-calendar",
  component: MyCalendar,
  staticData: {
    handle: { title: "My Calendar" },
  },
});

const monthlyCoveragePlanMyCalendarByMonthYearRoute = createRoute({
  getParentRoute: () => deploymentRoute,
  path: "my-calendar/$month/$year",
  component: MyCalendar,
  staticData: {
    handle: { title: "My Calendar" },
  },
});

const monthlyCoveragePlanSearchIndexRoute = createRoute({
  getParentRoute: () => monthlyCoveragePlanSearchRoute,
  path: "/",
  component: MonthlyCoveragePlanSearchForm,
  staticData: {
    handle: { title: "Monthly Coverage Plan" },
  },
});

const monthlyCoveragePlanCreateRoute = createRoute({
  getParentRoute: () => monthlyCoveragePlanSearchRoute,
  path: "create",
  loader: async () => {
    // MOCK USER DATA
    const USER_DATA = {
      CoordinatorId: '1f2673d5-3578-4c6c-b795-42214f5116e5',
      CoordinatorName: 'Juan Dela Cruz',
      AreaId: 1,
      AreaName: 'Metro Manila',
      RoleIds: [1,2,3,4,5]
    }
    
    const areas = await getAllAreas();

    const now = new Date();
    now.setDate(1); 
    now.setMonth(now.getMonth() + 1);
    const nextMonth = now.getMonth() + 1;
    const nextYear = now.getFullYear();

    const defaultValues: MonthlyCoveragePlanDto = {
      coordinatorId: USER_DATA.CoordinatorId,
      coordinatorName: USER_DATA.CoordinatorName,
      monthId: nextMonth,
      monthName: now.toLocaleString('default', { month: 'long' }),
      areaId: areas[0].id,
      areaName: areas[0].name,
      year: nextYear,
      statusId: 1,
      statusName: "Draft",
      mcpSiteAssignments: [] 
    };

    const created = await createMonthlyCoveragePlan({
      payload: defaultValues,
    });
 
    throw redirect({
      to: "/deployment/monthly-coverage-plan/$id/edit",
      params: { id: created.id! },
      replace: true,
    });
  },
  pendingComponent: () => <PlaceholderPage title="Monthly Coverage Plan" />,
  component: () => null,
  staticData: {
    handle: { title: "Create Monthly Coverage Plan Request", action: "create" },
  },
});

const monthlyCoveragePlanDisplayRoute = createRoute({
  getParentRoute: () => monthlyCoveragePlanSearchRoute,
  path: "$id/display",
  component: MonthlyCoveragePlanForm,
  staticData: {
    handle: { title: "Display Monthly Coverage Plan Request", action: "display" },
  },
});

const monthlyCoveragePlanEditRoute = createRoute({
  getParentRoute: () => monthlyCoveragePlanSearchRoute,
  path: "$id/edit",
  component: MonthlyCoveragePlanForm,
  staticData: {
    handle: { title: "Edit Monthly Coverage Plan Request", action: "edit" },
  },
});

const scheduleAndSiteChangeRequestSearchRoute = createRoute({
  getParentRoute: () => deploymentRoute,
  path: "schedule-change",
  component: Outlet,
  staticData: {
    handle: { title: "Schedule & Site Change Request" },
  },
});

const scheduleAndSiteChangeRequestSearchIndexRoute = createRoute({
  getParentRoute: () => scheduleAndSiteChangeRequestSearchRoute,
  path: "/",
  component: ScheduleAndSiteChangeRequestSearchForm,
  staticData: {
    handle: { title: "Schedule & Site Change Request" },
  },
});

const scheduleAndSiteChangeRequestCreateRoute = createRoute({
  getParentRoute: () => scheduleAndSiteChangeRequestSearchRoute,
  path: "create",
  component: ScheduleAndSiteChangeRequestForm,
  staticData: {
    handle: { title: "Create Schedule & Site Change Request", action: "create" },
  },
});

const scheduleAndSiteChangeRequestDisplayRoute = createRoute({
  getParentRoute: () => scheduleAndSiteChangeRequestSearchRoute,
  path: "$id/display",
  component: ScheduleAndSiteChangeRequestForm,
  staticData: {
    handle: { title: "Display Schedule & Site Change Request", action: "display" },
  },
});

const scheduleAndSiteChangeRequestEditRoute = createRoute({
  getParentRoute: () => scheduleAndSiteChangeRequestSearchRoute,
  path: "$id/edit",
  component: ScheduleAndSiteChangeRequestForm,
  staticData: {
    handle: { title: "Edit Schedule & Site Change Request", action: "edit" },
  },
});

const deploymentRouteTree = deploymentRoute.addChildren([
  dailyActivityMonitoringLogRoute.addChildren([
    dailyActivityMonitoringLogIndexRoute,
    dailyActivityMonitoringLogDisplayRoute,
    dailyActivityMonitoringLogEditRoute,
  ]),
  monthlyCoveragePlanMyCalendarRoute,
  monthlyCoveragePlanMyCalendarByMonthYearRoute,
  concernsIssuesRoute,
  monthlyCoveragePlanSearchRoute.addChildren([
    monthlyCoveragePlanSearchIndexRoute,
    monthlyCoveragePlanCreateRoute,
    monthlyCoveragePlanDisplayRoute,
    monthlyCoveragePlanEditRoute,
  ]),
  scheduleAndSiteChangeRequestSearchRoute.addChildren([
    scheduleAndSiteChangeRequestSearchIndexRoute,
    scheduleAndSiteChangeRequestCreateRoute,
    scheduleAndSiteChangeRequestDisplayRoute,
    scheduleAndSiteChangeRequestEditRoute
  ]),
]);

export default deploymentRouteTree;
